import posthog from "posthog-js";
import React, { FC, useEffect, useState } from "react";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, IconButton, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";

import Quota from "../../../components/Quota";
import SaveButton from "../../../components/SaveButton";
import SettingSection from "../../../components/SettingSection";
import TextField from "../../../components/styled/TextField";
import { useDialog } from "../../../hooks/use-dialog";
import useQuota from "../../../hooks/use-quota";
import { pushSuccess } from "../../../slices/notifications";
import { setBannerMessage } from "../../../slices/pricing_info";
import { useDispatch, useSelector } from "../../../store";
import { useCreateOrganizationInviteMutation } from "../../../store/pomeriumApi";

const InviteUsers: FC = () => {
  const [inviteUser, inviteResult] = useCreateOrganizationInviteMutation();
  const dispatch = useDispatch();
  const dialog = useDialog();
  const [emails, setEmails] = useState<string>("");
  const { organizationId } = useSelector((state) => state.current);
  const { count: administratorCount, limit: administratorLimit } =
    useQuota("administrators");

  useEffect(() => {
    if (!dialog.open) {
      setEmails("");
    }
  }, [dialog.open]);

  useEffect(() => {
    if (inviteResult.isSuccess) {
      dispatch(pushSuccess("User(s) Invited!"));
      dialog.handleClose();
    }
  }, [inviteResult]);

  const handleSave = async () => {
    const splitEmails = emails.split(/[,\s]+/).filter((email) => email !== "");
    posthog.capture("invited_users", { emails: splitEmails });
    await inviteUser({
      organizationId,
      createOrganizationInviteRequest: {
        role: "admin",
        emails: splitEmails,
      },
    });
  };

  const handleClickInvite = (evt: React.MouseEvent): void => {
    evt.preventDefault();
    if (administratorCount >= administratorLimit) {
      dispatch(
        setBannerMessage("To add another administrator, upgrade your account."),
      );
    } else {
      dialog.handleOpen();
    }
  };

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Quota quota="administrators" label="admins" />
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddCircleOutlineIcon />}
        onClick={handleClickInvite}
      >
        Invite Users
      </Button>
      <Dialog
        open={dialog.open}
        onClose={dialog.handleClose}
        scroll="paper"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          Invite Users
          <IconButton
            color="primary"
            onClick={() => dialog.handleClose()}
            size="large"
            sx={{ ml: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Container disableGutters>
            <Stack>
              <SettingSection label="Emails" docsKey="">
                <TextField
                  multiline
                  rows={3}
                  error={!emails}
                  fullWidth
                  value={emails}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmails(e.target.value)
                  }
                />
              </SettingSection>
            </Stack>
          </Container>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => dialog.handleClose()}>Cancel</Button>
          <SaveButton color="primary" onClick={handleSave} disabled={!emails}>
            Invite Users
          </SaveButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default InviteUsers;
